/* eslint-disable react-refresh/only-export-components -- context + provider + hook are intentionally colocated in one module */
import { useAppSelector } from "@template/state";
import { ROUTES, type Role } from "@template/types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { useNavigate } from "react-router-dom";
import { FINANCE_NAV_ROUTE_BY_ID } from "./financeNav";
import {
  activeVer,
  buildModel,
  checksum,
  clone,
  genVouchers,
  now,
  rangeFor,
  today,
} from "./data/model";
import { CURRENCIES, CUR_MONTH_IDX, SCENARIOS, SEED } from "./data/seedData";
import type {
  AuditEntry,
  BudgetModel,
  BudgetPlanState,
  CommentaryEntry,
  CurrencyCode,
  CurrencyInfo,
  FrozenEditTarget,
  LastChange,
  LockInfo,
  RevisionBase,
  Scenario,
  SnapshotEntry,
  StagedVersion,
  VersionRecord,
  VoucherModalData,
} from "./data/types";
import { DEFAULT_ROLE_PERMS, ROLES, hasPerm, type RolePermissions } from "./permissions";

export interface FinanceCtx {
  role: Role;
  userName: string;
  userEmail: string;
  has: (permKey: string) => boolean;

  model: BudgetModel;
  setModel: Dispatch<SetStateAction<BudgetModel>>;

  cur: CurrencyCode;
  setCur: (c: CurrencyCode) => void;
  C: CurrencyInfo;
  scenario: Scenario;
  setScenario: (s: Scenario) => void;
  scf: number;
  fmt: (usd: number) => string;
  toDisp: (usd: number) => number;
  fromDisp: (v: number | string) => number;

  selMonth: number;
  setSelMonth: (m: number) => void;

  saved: boolean;
  flash: () => void;

  editItem: (
    id: string,
    kind: "planned" | "actual",
    mIdx: number,
    dispVal: number | string,
  ) => void;
  editRev: (id: string, kind: "planned" | "actual", mIdx: number, dispVal: number | string) => void;
  editAnnual: (id: string, dispVal: number | string) => void;

  locks: (LockInfo | null)[];
  doFreeze: (level: LockInfo["level"], anchor: number) => void;
  doUnlock: (m: number) => void;

  frozenEdits: Record<string, { ver: string; by: string; date: string; reason: string }>;
  frozenEdit: FrozenEditTarget | null;
  setFrozenEdit: (fe: FrozenEditTarget | null) => void;
  applyFrozenEdit: (fe: FrozenEditTarget, newDispVal: number | string, reason: string) => void;

  versions: VersionRecord[];
  setVersions: Dispatch<SetStateAction<VersionRecord[]>>;
  nextVersion: (mn?: string) => string;
  approveRevision: (id: string) => void;

  staged: StagedVersion | null;
  setStaged: Dispatch<SetStateAction<StagedVersion | null>>;

  lastChange: LastChange;
  setLastChange: Dispatch<SetStateAction<LastChange>>;

  snapshots: SnapshotEntry[];
  setSnapshots: Dispatch<SetStateAction<SnapshotEntry[]>>;

  audit: AuditEntry[];
  logAudit: (action: string, detail: string, reason?: string) => void;

  commentary: CommentaryEntry[];
  setCommentary: Dispatch<SetStateAction<CommentaryEntry[]>>;

  rolePerms: RolePermissions;
  setRolePerms: Dispatch<SetStateAction<RolePermissions>>;
  roles: string[];
  setRoles: Dispatch<SetStateAction<string[]>>;

  voucher: VoucherModalData | null;
  openVouchers: (it: BudgetModel["items"][number], scope: "cm" | "ytd", mIdx?: number) => void;
  closeVouchers: () => void;

  budgetPlan: BudgetPlanState;
  bpSaveDraft: () => void;
  bpSubmit: () => void;
  bpWithdraw: () => void;
  bpDecision: (
    level: "Finance Head" | "Director" | "CEO",
    decision: "Approved" | "Rejected" | "Changes",
    reason: string,
  ) => void;
  bpFreeze: () => void;
  bpPublish: () => void;
  bpRevision: () => void;
  bpDiscard: () => void;
  revisionBase: RevisionBase | null;
  setPage: (path: string) => void;
}

const FinanceContext = createContext<FinanceCtx | null>(null);

export function useFinance(): FinanceCtx {
  const ctx = useContext(FinanceContext);
  if (!ctx) throw new Error("useFinance() must be used within <FinanceProvider>");
  return ctx;
}

function bpBump(v: string): string {
  const p = v.replace("V", "").split(".").map(Number);
  return "V" + (p[0] ?? 1) + "." + ((p[1] ?? 0) + 1);
}

export function FinanceProvider({ children }: { children: ReactNode }) {
  const authUser = useAppSelector((s) => s.auth.user);
  const role: Role = authUser?.role ?? "Viewer";
  const userName = authUser?.displayName ?? authUser?.email ?? "—";
  const userEmail = authUser?.email ?? "—";

  const [model, setModel] = useState<BudgetModel>(buildModel);
  const [saved, setSaved] = useState(false);
  const [cur, setCur] = useState<CurrencyCode>("USD");
  const [scenario, setScenario] = useState<Scenario>("Approved");
  const [selMonth, setSelMonth] = useState(CUR_MONTH_IDX);

  const [voucher, setVoucher] = useState<VoucherModalData | null>(null);
  const [frozenEdit, setFrozenEdit] = useState<FrozenEditTarget | null>(null);

  const [locks, setLocks] = useState<(LockInfo | null)[]>(() => {
    const a: (LockInfo | null)[] = Array(12).fill(null);
    a[0] = { level: "Quarter", by: "System", date: today() };
    a[1] = { level: "Quarter", by: "System", date: today() };
    a[2] = { level: "Quarter", by: "System", date: today() };
    return a;
  });
  const [frozenEdits, setFrozenEdits] = useState<FinanceCtx["frozenEdits"]>({});

  const [versions, setVersions] = useState<VersionRecord[]>(() => [
    {
      id: "v0",
      ver: "V1.0",
      label: "Original Annual Budget",
      month: "—",
      fy: SEED.fiscalYear,
      kind: "Budget",
      active: true,
      parentVersion: "",
      uploadedBy: "System",
      uploadDate: today(),
      status: "Published",
      checksum: "sha-baseline",
      remarks: "Board-approved opening budget",
      summary: { total: 79, valid: 79, warning: 0, error: 0, imported: 79, skipped: 0 },
      changeCount: 0,
      approvals: { "Finance Head": "Approved", Director: "Approved", CEO: "Approved" },
      comments: [],
      approvedDate: today(),
      freezeDate: today(),
      publishedDate: today(),
      snapshot: buildModel(),
      reason: "Original approval",
    },
  ]);
  const [staged, setStaged] = useState<StagedVersion | null>(null);
  const [lastChange, setLastChange] = useState<LastChange>({
    version: "V1.0",
    by: "System",
    date: today(),
    cells: {},
    pages: {},
    counts: { added: 0, modified: 0, unchanged: 0 },
  });
  const [snapshots, setSnapshots] = useState<SnapshotEntry[]>([
    { id: "s0", name: "FY Opening", date: today(), ver: "V1.0", data: null },
  ]);
  const [audit, setAudit] = useState<AuditEntry[]>([
    {
      id: "a0",
      action: "Baseline published",
      by: "System",
      role: "System",
      ver: "V1.0",
      date: today(),
      ip: "system",
      reason: "Original approval",
      detail: "V1.0 opening budget activated",
    },
  ]);
  const [commentary, setCommentary] = useState<CommentaryEntry[]>([
    {
      id: "c0",
      by: "Priya Nair",
      role: "Director",
      date: today(),
      text: "Q1 locked post board approval. R&D over-run to be monitored through H1.",
    },
  ]);

  const [rolePerms, setRolePerms] = useState<RolePermissions>(() => clone(DEFAULT_ROLE_PERMS));
  const [roles, setRoles] = useState<string[]>(ROLES.slice());

  const [budgetPlan, setBudgetPlan] = useState<BudgetPlanState>({
    status: "Published",
    version: "V1.0",
    verId: "v0",
    parentVersion: "",
    approvals: { "Finance Head": "Approved", Director: "Approved", CEO: "Approved" },
    submittedBy: "System",
    submittedDate: today(),
    approvedDate: today(),
    publishedDate: today(),
    lastSaved: today(),
    reason: "Original board approval",
  });
  const [revisionBase, setRevisionBase] = useState<RevisionBase | null>(null);

  const has = useCallback(
    (permKey: string) => hasPerm(rolePerms, role, permKey),
    [rolePerms, role],
  );

  const C = CURRENCIES[cur];
  const fmt = useCallback(
    (usd: number) =>
      new Intl.NumberFormat(C.locale, {
        style: "currency",
        currency: cur,
        maximumFractionDigits: 0,
      }).format((usd || 0) * C.rate),
    [C, cur],
  );
  const toDisp = useCallback((usd: number) => Math.round((usd || 0) * C.rate), [C]);
  const fromDisp = useCallback((v: number | string) => (Number(v) || 0) / C.rate, [C]);
  const scf = SCENARIOS[scenario] || 1;

  const flashTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const flash = useCallback(() => {
    setSaved(true);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setSaved(false), 1200);
  }, []);
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const activeVerLocal = useCallback(() => activeVer(versions), [versions]);

  const logAudit = useCallback(
    (action: string, detail: string, reason = "") => {
      setAudit((a) => [
        {
          id: "a" + Date.now() + Math.random(),
          action,
          by: userName,
          role,
          ver: activeVerLocal(),
          date: now(),
          ip: "client-session",
          reason,
          detail,
        },
        ...a,
      ]);
    },
    [userName, role, activeVerLocal],
  );

  const editItem = useCallback(
    (id: string, kind: "planned" | "actual", mIdx: number, dispVal: number | string) => {
      setModel((prev) => {
        const n = clone(prev);
        const r = n.items.find((x) => x.id === id);
        if (r) r[kind][mIdx] = fromDisp(dispVal);
        return n;
      });
      flash();
    },
    [fromDisp, flash],
  );

  const editRev = useCallback(
    (id: string, kind: "planned" | "actual", mIdx: number, dispVal: number | string) => {
      setModel((prev) => {
        const n = clone(prev);
        const r = n.revenue.find((x) => x.id === id);
        if (r) r[kind][mIdx] = fromDisp(dispVal);
        return n;
      });
      flash();
    },
    [fromDisp, flash],
  );

  const editAnnual = useCallback(
    (id: string, dispVal: number | string) => {
      setModel((prev) => {
        const n = clone(prev);
        const r = n.items.find((x) => x.id === id);
        if (r) {
          const oldA = r.planned.reduce((s, v) => s + v, 0);
          const newA = fromDisp(dispVal);
          if (oldA > 0) {
            const f = newA / oldA;
            r.planned = r.planned.map((v) => Math.round(v * f));
          } else {
            r.planned = r.planned.map(() => Math.round(newA / 12));
          }
        }
        return n;
      });
      flash();
    },
    [fromDisp, flash],
  );

  const nextVersion = useCallback(
    (mn?: string) => {
      const pub = versions.filter((v) => v.status === "Published");
      const last = pub[pub.length - 1] ?? { ver: "V1.0" };
      const pr = last.ver.replace("V", "").split(".").map(Number);
      const isNew = Boolean(mn) && !versions.some((v) => v.month === mn);
      return isNew
        ? "V" + ((pr[0] ?? 1) + 1) + ".0"
        : "V" + (pr[0] ?? 1) + "." + ((pr[1] ?? 0) + 1);
    },
    [versions],
  );

  const openVouchers = useCallback(
    (it: BudgetModel["items"][number], scope: "cm" | "ytd", mIdx?: number) => {
      const M = mIdx ?? selMonth;
      const fromM = scope === "cm" ? M : 0;
      setVoucher({ it, vouchers: genVouchers(it, fromM, M), scope, fromM, toM: M });
    },
    [selMonth],
  );
  const closeVouchers = useCallback(() => setVoucher(null), []);

  const doFreeze = useCallback(
    (level: LockInfo["level"], anchor: number) => {
      const r = rangeFor(level, anchor);
      setLocks((prev) => {
        const n = prev.slice();
        r.forEach((m) => (n[m] = { level, by: userName, date: today() }));
        return n;
      });
      const first = r[0] ?? 0;
      const last = r[r.length - 1] ?? first;
      logAudit(
        "Period frozen",
        level +
          " lock applied to " +
          (SEED.months[first] ?? "") +
          (r.length > 1 ? " – " + (SEED.months[last] ?? "") : ""),
      );
    },
    [userName, logAudit],
  );
  const doUnlock = useCallback(
    (m: number) => {
      setLocks((prev) => {
        const n = prev.slice();
        n[m] = null;
        return n;
      });
      logAudit("Period reopened", (SEED.months[m] ?? "") + " unlocked (reopen)");
    },
    [logAudit],
  );

  const applyFrozenEdit = useCallback(
    (fe: FrozenEditTarget, newDispVal: number | string, reason: string) => {
      const newUSD = fromDisp(newDispVal);
      const ver = nextVersion();
      const key = fe.it.id + "|" + fe.field + "|" + fe.m;
      let snap: BudgetModel = model;
      setModel((prev) => {
        const n = clone(prev);
        const r = n.items.find((x) => x.id === fe.it.id);
        if (r) r[fe.field][fe.m] = newUSD;
        snap = n;
        return n;
      });
      setFrozenEdits((prev) => ({ ...prev, [key]: { ver, by: userName, date: today(), reason } }));
      setVersions((vs) => [
        ...vs,
        {
          id: "rev" + Date.now(),
          ver,
          label: "Frozen edit — " + fe.it.name + " (" + (SEED.months[fe.m] ?? "") + ")",
          month: SEED.months[fe.m] ?? "",
          status: "Revision",
          uploadedBy: userName,
          uploadDate: now(),
          publishedDate: "",
          checksum: checksum(key + newUSD),
          reason,
          dualApprovers: [],
          changeCount: 1,
          summary: { total: 1 },
          snapshot: snap,
          change: {
            code: fe.it.led,
            name: fe.it.name,
            field: fe.field,
            month: SEED.months[fe.m] ?? "",
            old: fe.it[fe.field][fe.m] ?? 0,
            new: newUSD,
          },
        },
      ]);
      logAudit(
        "Frozen period modified",
        fe.it.name + " " + (SEED.months[fe.m] ?? "") + " → " + ver + " (pending dual approval)",
        reason,
      );
      setFrozenEdit(null);
      flash();
    },
    [fromDisp, nextVersion, userName, logAudit, flash, model],
  );

  const approveRevision = useCallback(
    (id: string) => {
      setVersions((vs) =>
        vs.map((v) => {
          if (v.id !== id) return v;
          const ap = (v.dualApprovers ?? []).slice();
          if (ap.some((x) => x.role === role)) return v;
          ap.push({ role, name: userName, date: now() });
          const nv: VersionRecord = { ...v, dualApprovers: ap };
          if (ap.length >= 2) {
            nv.status = "Published";
            nv.publishedDate = now();
          }
          return nv;
        }),
      );
      logAudit("Revision approval", "Dual approval by " + role);
    },
    [role, userName, logAudit],
  );

  const bpSaveDraft = useCallback(() => {
    setBudgetPlan((b) => ({ ...b, lastSaved: now() }));
    logAudit("Budget draft saved", budgetPlan.version + " draft saved");
  }, [logAudit, budgetPlan.version]);

  const bpSubmit = useCallback(() => {
    setBudgetPlan((b) => ({
      ...b,
      status: "Under Review",
      submittedBy: userName,
      submittedDate: now(),
      approvals: { "Finance Head": null, Director: null, CEO: null },
    }));
    logAudit("Budget submitted for approval", budgetPlan.version + " submitted by " + userName);
  }, [userName, logAudit, budgetPlan.version]);

  const bpWithdraw = useCallback(() => {
    setBudgetPlan((b) => ({
      ...b,
      status: "Draft",
      approvals: { "Finance Head": null, Director: null, CEO: null },
    }));
    logAudit("Submission withdrawn", budgetPlan.version + " returned to Draft by " + userName);
  }, [logAudit, budgetPlan.version, userName]);

  const bpDecision = useCallback(
    (
      level: "Finance Head" | "Director" | "CEO",
      decision: "Approved" | "Rejected" | "Changes",
      reason: string,
    ) => {
      const na = { ...budgetPlan.approvals, [level]: decision };
      const willApprove =
        decision === "Approved" &&
        (["Finance Head", "Director"] as const).every((l) => na[l] === "Approved");
      let newId: string | null = null;
      setBudgetPlan((b) => {
        const n = clone(b);
        n.approvals[level] = decision;
        if (decision !== "Approved") {
          n.status = "Draft";
          n.reason = reason || "";
        } else if (willApprove) {
          n.status = "Approved";
          n.approvedDate = now();
          newId = "bp" + Date.now();
          n.verId = newId;
        } else {
          n.status = "Under Review";
        }
        return n;
      });
      logAudit(
        "Budget " +
          (decision === "Approved"
            ? "approval"
            : decision === "Rejected"
              ? "rejected"
              : "changes requested"),
        budgetPlan.version + " — " + level + (reason ? ' · "' + reason + '"' : ""),
        reason || "",
      );
      if (willApprove) {
        setVersions((vs) => [
          ...vs,
          {
            id: newId ?? "bp" + Date.now(),
            ver: budgetPlan.version,
            label:
              "Budget Plan " +
              budgetPlan.version +
              (budgetPlan.parentVersion ? " (revision of " + budgetPlan.parentVersion + ")" : ""),
            month: "—",
            fy: SEED.fiscalYear,
            kind: "Budget",
            status: "Approved",
            active: false,
            parentVersion: budgetPlan.parentVersion || "",
            uploadedBy: userName,
            uploadDate: now(),
            approvedDate: now(),
            checksum: "sha-bp" + Date.now().toString(16),
            reason: budgetPlan.reason || "Annual plan revision",
            changeCount: 0,
            summary: { total: model.items.length },
            snapshot: clone(model),
          },
        ]);
        logAudit(
          "Budget approved",
          budgetPlan.version + " approved (workflow complete) — ready to freeze",
        );
      }
    },
    [budgetPlan, logAudit, userName, model],
  );

  const bpFreeze = useCallback(() => {
    if (budgetPlan.verId) {
      const id = budgetPlan.verId;
      setVersions((vs) =>
        vs.map((v) => (v.id === id ? { ...v, status: "Frozen", freezeDate: now() } : v)),
      );
    }
    setBudgetPlan((b) => ({ ...b, status: "Frozen" }));
    logAudit("Budget frozen", budgetPlan.version + " locked against further edits");
  }, [budgetPlan.verId, budgetPlan.version, logAudit]);

  const bpPublish = useCallback(() => {
    const id = budgetPlan.verId;
    setVersions((vs) =>
      vs.map((v) =>
        v.kind === "Budget"
          ? {
              ...v,
              active: v.id === id,
              status: v.id === id ? "Published" : v.active ? "Frozen" : v.status,
              publishedDate: v.id === id ? now() : v.publishedDate,
            }
          : v,
      ),
    );
    setBudgetPlan((b) => ({ ...b, status: "Published", publishedDate: now() }));
    logAudit(
      "Budget published",
      budgetPlan.version +
        " is now the ACTIVE official budget (drives Dashboard, Budget vs Actual, Reports)",
    );
  }, [budgetPlan.verId, budgetPlan.version, logAudit]);

  const bpRevision = useCallback(() => {
    const nv = bpBump(budgetPlan.version);
    setRevisionBase({
      version: budgetPlan.version,
      status: budgetPlan.status,
      verId: budgetPlan.verId,
      snapshot: clone(model),
    });
    setBudgetPlan((b) => ({
      ...b,
      status: "Draft",
      version: nv,
      parentVersion: budgetPlan.version,
      verId: null,
      approvals: { "Finance Head": null, Director: null, CEO: null },
      submittedBy: "",
      submittedDate: "",
      reason: "",
    }));
    logAudit(
      "Budget revision created",
      nv + " draft created from " + budgetPlan.version + " (parent kept read-only)",
    );
  }, [budgetPlan, model, logAudit]);

  const bpDiscard = useCallback(() => {
    if (!revisionBase) return;
    setModel(clone(revisionBase.snapshot));
    setBudgetPlan((b) => ({
      ...b,
      status: revisionBase.status,
      version: revisionBase.version,
      verId: revisionBase.verId,
      parentVersion: "",
      approvals: { "Finance Head": "Approved", Director: "Approved", CEO: "Approved" },
      submittedBy: "",
      submittedDate: "",
      reason: "Reverted to " + revisionBase.version,
    }));
    logAudit(
      "Budget draft discarded",
      "Draft " +
        budgetPlan.version +
        " discarded — reverted to " +
        revisionBase.version +
        " (" +
        revisionBase.status +
        ")",
    );
    setRevisionBase(null);
  }, [revisionBase, logAudit, budgetPlan.version]);

  const navigate = useNavigate();
  /** Ported as `ctx.setPage(pageId)` in the source prototype — now a real route navigation. */
  const setPage = useCallback(
    (pageId: string) => {
      navigate(FINANCE_NAV_ROUTE_BY_ID[pageId] ?? ROUTES.financeDashboard);
    },
    [navigate],
  );

  const value = useMemo<FinanceCtx>(
    () => ({
      role,
      userName,
      userEmail,
      has,
      model,
      setModel,
      cur,
      setCur,
      C,
      scenario,
      setScenario,
      scf,
      fmt,
      toDisp,
      fromDisp,
      selMonth,
      setSelMonth,
      saved,
      flash,
      editItem,
      editRev,
      editAnnual,
      locks,
      doFreeze,
      doUnlock,
      frozenEdits,
      frozenEdit,
      setFrozenEdit,
      applyFrozenEdit,
      versions,
      setVersions,
      nextVersion,
      approveRevision,
      staged,
      setStaged,
      lastChange,
      setLastChange,
      snapshots,
      setSnapshots,
      audit,
      logAudit,
      commentary,
      setCommentary,
      rolePerms,
      setRolePerms,
      roles,
      setRoles,
      voucher,
      openVouchers,
      closeVouchers,
      budgetPlan,
      bpSaveDraft,
      bpSubmit,
      bpWithdraw,
      bpDecision,
      bpFreeze,
      bpPublish,
      bpRevision,
      bpDiscard,
      revisionBase,
      setPage,
    }),
    [
      role,
      userName,
      userEmail,
      has,
      model,
      cur,
      C,
      scenario,
      scf,
      fmt,
      toDisp,
      fromDisp,
      selMonth,
      saved,
      flash,
      editItem,
      editRev,
      editAnnual,
      locks,
      doFreeze,
      doUnlock,
      frozenEdits,
      frozenEdit,
      applyFrozenEdit,
      versions,
      nextVersion,
      approveRevision,
      staged,
      lastChange,
      snapshots,
      audit,
      logAudit,
      commentary,
      rolePerms,
      roles,
      voucher,
      openVouchers,
      closeVouchers,
      budgetPlan,
      bpSaveDraft,
      bpSubmit,
      bpWithdraw,
      bpDecision,
      bpFreeze,
      bpPublish,
      bpRevision,
      bpDiscard,
      revisionBase,
      setPage,
    ],
  );

  return <FinanceContext.Provider value={value}>{children}</FinanceContext.Provider>;
}
